/**
 * Tingkat antara (owner, 2026-10-05; release 16): the diocese's word for the one level between
 * the diocese and its parishes. Keuskupan Agats-Asmat says **Wilayah**, each headed by the **Ketua
 * wilayah**; **Dekenat**, headed by the **Dekan**, stays the other value and keeps working. It is
 * a deployment setting, since SATU serves one diocese: `SATU_TINGKAT_ANTARA` for the API, read at
 * run time, and `VITE_SATU_TINGKAT_ANTARA` for the web build; unset means wilayah, any other value
 * means wilayah too and the host warns once.
 *
 * Only words change. The level stays one level with the stored codes it always had
 * (`unit_teritorial.jenis = 'dekenat'`, the position `dekan`, the role `dekan`,
 * `pengguna.dekenat_id`). The strings carry the words as placeholders: `{tingkat}` as a name or
 * at a sentence's start ("Wilayah", "Dekenat"), `{tingkatKecil}` inside a sentence, and the head
 * of the level as `{kepalaTingkat}` ("Ketua Wilayah", "Dekan", a role's name as "Ketua Stasi" is,
 * buttons included: "Tambah Ketua Wilayah") and `{kepalaTingkatKecil}` ("ketua wilayah") where a
 * sentence needs it lower. Indonesian needs no plural ("2 wilayah"). Names typed by people
 * ("Wilayah Agats") stay as typed.
 *
 * Pure: each host reads its setting once and hands the words to what it fills (the app:
 * apps/web/src/lib/tingkat.ts; the API: `readEnv` in apps/api/src/env.ts into `deps.tingkat`,
 * read through `tingkatDari` in apps/api/src/sebutan.ts).
 */
import ui from './ui.id.json' with { type: 'json' }

export const TINGKAT_ANTARA = ['wilayah', 'dekenat'] as const
export type TingkatAntara = (typeof TINGKAT_ANTARA)[number]

/** Keuskupan Agats-Asmat's word, so production needs no variable. */
export const TINGKAT_ANTARA_BAWAAN: TingkatAntara = 'wilayah'

export interface SebutanTingkat {
  readonly nilai: TingkatAntara
  /** As a name or at a sentence's start: "Wilayah", "Dekenat". */
  readonly tingkat: string
  /** Inside a sentence: "wilayah", "dekenat". */
  readonly tingkatKecil: string
  /** Its head, as a name or at a sentence's start: "Ketua Wilayah", "Dekan". */
  readonly kepalaTingkat: string
  /** Its head inside a sentence: "ketua wilayah", "dekan". */
  readonly kepalaTingkatKecil: string
}

function kenal(nilai: string): nilai is TingkatAntara {
  return (TINGKAT_ANTARA as readonly string[]).includes(nilai)
}

/**
 * The setting as a host reads it: unset or blank is the default; either value in any case; any
 * other value is the default too, and `peringatan` hears why, without the value itself (a value
 * typed in the wrong field may be a secret).
 */
export function bacaTingkatAntara(
  mentah: string | null | undefined,
  peringatan?: (pesan: string) => void,
): TingkatAntara {
  const nilai = (mentah ?? '').trim().toLowerCase()
  if (nilai === '') return TINGKAT_ANTARA_BAWAAN
  if (kenal(nilai)) return nilai
  peringatan?.(
    `SATU_TINGKAT_ANTARA: nilai tidak dikenal, dipakai ${TINGKAT_ANTARA_BAWAAN} (pilihan: ${TINGKAT_ANTARA.join(', ')})`,
  )
  return TINGKAT_ANTARA_BAWAAN
}

/**
 * The web build's value (apps/web/vite.config.ts; release 16 review M4): its own
 * VITE_SATU_TINGKAT_ANTARA when set, else the API's SATU_TINGKAT_ANTARA, so one variable is
 * enough. Both set to different words: the build's own wins and `peringatan` hears it once, with
 * the two words read (never the raw values). An unknown value warns under its own name.
 */
export function tingkatBangun(
  vite: string | null | undefined,
  api: string | null | undefined,
  peringatan?: (pesan: string) => void,
): TingkatAntara {
  const ada = (v: string | null | undefined) => (v ?? '').trim() !== ''
  if (!ada(vite)) return bacaTingkatAntara(api, peringatan)
  const nilai = bacaTingkatAntara(vite, (pesan) => peringatan?.(`VITE_${pesan}`))
  const server = ada(api) ? bacaTingkatAntara(api) : nilai
  if (server !== nilai) {
    peringatan?.(
      `VITE_SATU_TINGKAT_ANTARA dan SATU_TINGKAT_ANTARA berbeda: aplikasi memakai ${nilai}, server memakai ${server}. Samakan keduanya, atau hapus VITE_SATU_TINGKAT_ANTARA.`,
    )
  }
  return nilai
}

const SEBUTAN: Readonly<Record<TingkatAntara, SebutanTingkat>> = {
  wilayah: { nilai: 'wilayah', ...ui.tingkatAntara.wilayah },
  dekenat: { nilai: 'dekenat', ...ui.tingkatAntara.dekenat },
}

/** The words of a value; anything else (none, unknown) is the default's. Silent: hosts warn. */
export function sebutanTingkat(nilai: string | null | undefined): SebutanTingkat {
  return SEBUTAN[bacaTingkatAntara(nilai)]
}

/** The default's words, for a test or a caller that names none. */
export const SEBUTAN_TINGKAT_BAWAAN: SebutanTingkat = SEBUTAN[TINGKAT_ANTARA_BAWAAN]

const PENANDA = /\{(tingkat|tingkatKecil|kepalaTingkat|kepalaTingkatKecil)\}/g

function isiTeks(teks: string, s: SebutanTingkat): string {
  return teks.includes('ingkat')
    ? teks.replace(PENANDA, (_, nama: keyof Omit<SebutanTingkat, 'nilai'>) => s[nama])
    : teks
}

const tersimpan = new WeakMap<object, Map<TingkatAntara, unknown>>()

/**
 * A string, or a tree of strings (a part of `uiStrings`), with the four placeholders filled;
 * every other placeholder stays for `format` or `isiUnit`. A tree comes back as the same object
 * for the same value, so a screen's memo sees it unchanged between renders.
 */
export function isiTingkat<T>(nilai: T, s: SebutanTingkat): T {
  if (typeof nilai === 'string') return isiTeks(nilai, s) as T
  if (typeof nilai !== 'object' || nilai === null) return nilai
  let perNilai = tersimpan.get(nilai)
  if (perNilai === undefined) {
    perNilai = new Map()
    tersimpan.set(nilai, perNilai)
  }
  const ada = perNilai.get(s.nilai)
  if (ada !== undefined) return ada as T
  const hasil = Array.isArray(nilai)
    ? nilai.map((v: unknown) => isiTingkat(v, s))
    : Object.fromEntries(Object.entries(nilai).map(([k, v]) => [k, isiTingkat(v, s)]))
  perNilai.set(s.nilai, hasil)
  return hasil as T
}

const AWALAN: Readonly<Record<TingkatAntara, RegExp>> = {
  wilayah: new RegExp(`^${SEBUTAN.wilayah.tingkat}\\s+(?=\\S)`, 'i'),
  dekenat: new RegExp(`^${SEBUTAN.dekenat.tingkat}\\s+(?=\\S)`, 'i'),
}

/**
 * A level unit's name without the value's own word it opens with, so a template that says the
 * word itself says it once: under wilayah "Wilayah Agats" gives "Agats". The other value's word
 * is part of the name and stays as typed (release 16 review M3): under wilayah "Dekenat Agats"
 * stays "Dekenat Agats". A name that is only the word stays whole.
 */
export function tanpaSebutanTingkat(
  nama: string,
  s: SebutanTingkat = SEBUTAN_TINGKAT_BAWAAN,
): string {
  return nama.replace(AWALAN[s.nilai], '')
}
